import React, { useEffect, useState } from "react";
import { PolicyProject } from "winslow-core";
import "./PolicyStudioViewer.css";
import { PolicyStudioActionButton } from "./components/PolicyStudioActionButton";
import { PolicyStudioIconButton } from "./components/PolicyStudioIconButton";

import { showdownConverter } from "winslow-core";
import { MyTooltip } from "../common/MyTooltip";
import { ModalConfirmEmbedPolicyAsResource } from "./ModalConfirmEmbedPolicyAsResource";
import { copyMarkdownToClipboard } from "../../hooks/usePolicyStudio";
import { HUBSPOT_SIGNUP_LINK } from "../../config";
import { Copy, Download } from "lucide-react";
import { ModalCreatingPolicyInterstitial } from "./ModalCreatingPolicyInterstitial";

import { PolicyTipTapEditor } from "./components/PolicyTipTapEditor";
import TurndownService from "turndown";
import { ModalUnsavedChanges } from "./ModalUnsavedChanges";

const turndownService = new TurndownService();

interface PolicyStudioViewerProps {
  policy: PolicyProject | null;
  isLoading: boolean;
  isPublic: boolean;
  handleAddNewClick: () => void;
  handleAddAsResource: (replaceExisting: boolean) => void;
  openConflictsDrawer: () => void;
  openComplianceDrawer: () => void;
  exportContentToPdf: (title: string, content: string) => void;
  updatePolicyContent?: (content: string) => void;
}

export const PolicyStudioViewer = ({
  policy,
  isLoading,
  isPublic,
  handleAddNewClick,
  handleAddAsResource,
  openConflictsDrawer,
  openComplianceDrawer,
  exportContentToPdf,
  updatePolicyContent,
}: PolicyStudioViewerProps) => {
  const [
    isConfirmEmbedPolicyAsResourceModalOpen,
    setIsConfirmEmbedPolicyAsResourceModalOpen,
  ] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editorContent, setEditorContent] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const handleCopy = async () => {
    if (policy) {
      copyMarkdownToClipboard(policy.content);
    }
  };

  const handleAddPolicyAsResource = async () => {
    if (isDirty) {
      setIsUnsavedChangesModalOpen(true);
      return;
    }

    if (policy && policy.originalLinkRecordId) {
      setIsConfirmEmbedPolicyAsResourceModalOpen(true);
    } else {
      handleAddAsResource(false);
    }
  };

  const formatPolicyContentToHtml = (content: string) => {
    const cleanContent = content
      .replace(/^```markdown\n/, "") // Remove opening markdown fence
      .replace(/\n```$/, ""); // Remove closing fence

    let htmlContent = showdownConverter.makeHtml(cleanContent);
    const regex = /\[(.*?)\]/g;
    const matches = htmlContent.match(regex);
    if (matches) {
      matches.forEach((match) => {
        htmlContent = htmlContent.replaceAll(
          match,
          `<span style="color: blue;">${match}</span>`
        );
      });
    }
    return htmlContent;
  };

  const handleEditorChange = (html: string) => {
    setEditedContent(html);
    setIsDirty(true);
  };

  const handleSave = () => {
    if (updatePolicyContent && policy) {
      // Convert HTML back to markdown
      const markdown = turndownService.turndown(editedContent);

      // Save to policy content
      updatePolicyContent(markdown);
      setIsDirty(false);
      setIsUnsavedChangesModalOpen(false);
    }
    setEditMode(false);
  };

  const handleCancel = () => {
    if (policy) {
      setEditorContent(formatPolicyContentToHtml(policy.content));
      setEditedContent(formatPolicyContentToHtml(policy.content));
      setIsDirty(false);
    }
    setEditMode(false);
  };

  const handleDiscardChanges = () => {
    handleCancel();
    setIsUnsavedChangesModalOpen(false);
  };

  useEffect(() => {
    if (policy) {
      const formattedContent = formatPolicyContentToHtml(policy.content);
      setEditorContent(formattedContent);
      setEditedContent(formattedContent);
      setIsDirty(false);
    }
  }, [policy]);

  // Exit edit mode when loading starts
  useEffect(() => {
    if (isLoading && editMode) {
      setEditMode(false);
    }
  }, [isLoading]);

  return (
    <div className="policy-viewer">
      <div className="policy-viewer-content">
        <div className="policy-viewer-main">
          <div className="policy-viewer-main-content">
            <div className="policy-viewer-action-container">
              <div className="policy-viewer-action-group">
                <div className="policy-viewer-action-group-left">
                  {isPublic && <h1>Winslow Policy Studio</h1>}
                  {!isPublic && (
                    <>
                      <PolicyStudioActionButton
                        key={0}
                        label={"Add to Resources"}
                        variant={"secondary"}
                        onClick={handleAddPolicyAsResource}
                        disabled={
                          isLoading || !policy || !!policy?.linkRecordId
                        }
                      />
                    </>
                  )}
                  <PolicyStudioActionButton
                    key={1}
                    label={"Find Conflicts"}
                    variant={"secondary"}
                    onClick={openConflictsDrawer}
                    disabled={isLoading || !policy}
                  />

                  <PolicyStudioActionButton
                    key={2}
                    label={"Compliance Check"}
                    variant={"secondary"}
                    onClick={openComplianceDrawer}
                    disabled={isLoading || !policy}
                  />
                </div>
                <div className="policy-viewer-icon-group">
                  {isPublic && (
                    <button
                      className="policy-viewer-cta-button"
                      onClick={() => {
                        window.open(HUBSPOT_SIGNUP_LINK, "_blank");
                      }}>
                      Talk to Sales
                    </button>
                  )}
                  {policy && (
                    <>
                      <MyTooltip title="Copy">
                        <PolicyStudioIconButton
                          key="1"
                          iconComponent={<Copy size={20} />}
                          border={false}
                          alt="copy"
                          onClick={() => handleCopy()}
                          isOnTop={true}
                          disabled={!policy}
                        />
                      </MyTooltip>
                      <MyTooltip title="Export">
                        <PolicyStudioIconButton
                          key="2"
                          iconComponent={<Download size={20} />}
                          border={false}
                          alt="export"
                          onClick={() => {
                            if (isDirty) {
                              setIsUnsavedChangesModalOpen(true);
                              return;
                            }

                            if (policy?.content) {
                              exportContentToPdf(policy.title, policy.content);
                            }
                          }}
                          isOnTop={true}
                          disabled={!policy}
                        />
                      </MyTooltip>
                    </>
                  )}
                </div>
              </div>
              <div className={`policy-viewer-card `}>
                {policy ? (
                  <PolicyTipTapEditor
                    content={editorContent}
                    editable={editMode}
                    isLoading={isLoading}
                    onChange={handleEditorChange}
                    onSave={handleSave}
                    onToggleEdit={() => setEditMode(true)}
                    onCancel={handleCancel}
                  />
                ) : isPublic ? (
                  <div className="policy-viewer-zero-state">
                    <div className="policy-viewer-zero-icon"></div>
                    <div className="policy-viewer-zero-text">
                      Create or select a policy to get started
                      <button onClick={handleAddNewClick}> Create </button>
                    </div>
                  </div>
                ) : (
                  <div className="policy-viewer-zero-state">
                    <div className="policy-viewer-zero-icon"></div>
                    <div className="policy-viewer-zero-text">
                      Create or select a policy to get started
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ModalConfirmEmbedPolicyAsResource
        isOpen={isConfirmEmbedPolicyAsResourceModalOpen}
        onClose={() => setIsConfirmEmbedPolicyAsResourceModalOpen(false)}
        onSubmit={handleAddAsResource}
      />
      <ModalUnsavedChanges
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => {
          setIsUnsavedChangesModalOpen(false);
        }}
        onSave={handleSave}
        onDiscard={handleDiscardChanges}
      />
      {isLoading && <ModalCreatingPolicyInterstitial isOpen={isLoading} />}
    </div>
  );
};
